import React, { useEffect, useState } from 'react';
import { useFormikContext } from 'formik';
import MoneyInput from './MoneyInput';
import UnitMoneyInput from './UnitMoneyInput';
import PercentTextInput from './PercentTextInput';
import RadioBigGroup from './RadioBigGroup';
import RadioButtons from './RadioButtons';
import Select from './Select';
import TextArea from './TextArea';
import TextInput from './TextInput';
import YesNo from './YesNo';
import YesNoExplain from './YesNoExplain';
import Explain from './Explain';
import GooglePlaces from './GooglePlacesAutoComplete';
import FormUIOptions from './formUIOptions';
import NumberInput from './NumberInput';
import Toggle from './Toggle';
import CalculatedField from './CalculatedField';
import TableInputModal from './TableInputModal';
import TableInputInline from './TableInputInline';
import GalleryInput from './PictureGallery';
import { checkCondition, isConditionLocal } from '../utility/filterSchema';
import BeforeAfterTableInput from './BeforeAfterTableInline';
import BeforeAfterTableModal from './BeforeAfterTableModal';
import calculateField from '../utility/calculateFields';
import TableSection from './TableSection';
import LinkButton from './LinkButton';
import UnitTimeInput from './UnitTimeInput';
import SubSectionHeader from './SubSectionHeader';
import CalculatedSection from './CalculatedSection';

export default function InputTypeSwitch({
  elem,
  externalData = {},
  sectionHeadersInline = [],
  sectionHeadersModal = [],
  embedVersion = false,
}) {
  const {
    condition,
    defaultLabel,
    defaultValue,
    derivedFrom,
    describeMessage,
    label,
    layout,
    placeholder,
    description,
    tooltipHeading,
    tooltipFormula,
    disabled,
    name,
    options,
    required = false,
    specialInstructions,
    selectOptions,
    type,
    unit,
    emphasize = false,
    showAltValue,
    tableSchema,
    schema,
    modalDescription,
    itemName,
    calcFunction,
    to,
    yesLabel,
    noLabel,
    maxValue,
    hideLabel,
    toggleColour,
    afterPosition,
  } = elem;

  let { preValue } = elem;

  // Check for conditions within the current section
  const [show, setShow] = useState(true);

  const { values } = useFormikContext();

  if (derivedFrom && calcFunction && !preValue) {
    preValue = calculateField(elem, { ...externalData, ...values });
  }

  useEffect(() => {
    if (condition) {
      const isLocal = isConditionLocal(condition);
      if (isLocal) {
        const conditionResult = checkCondition(condition, {
          ...externalData,
          ...values,
        });
        setShow(conditionResult);
      } else {
        const conditionResult = checkCondition(condition, {
          ...externalData,
          ...values,
        });
        setShow(conditionResult);
      }
    }
  }, [values, externalData]);

  if (embedVersion && elem.excludeFromEmbeddedForm) {
    return null;
  }

  if (!embedVersion && elem.onlyInEmbeddedForm) {
    return null;
  }

  const getInput = () => {
    switch (type) {
      case FormUIOptions.Text:
      case FormUIOptions.Email:
      case FormUIOptions.Telephone:
        return (
          <TextInput
            name={name}
            label={label}
            description={description}
            tooltipFormula={tooltipFormula}
            tooltipHeading={tooltipHeading}
            type={type}
            layout={layout}
            placeholder={placeholder}
            required={required}
            emphasize={emphasize}
            preValue={preValue}
            disabled={disabled}
          />
        );
      case FormUIOptions.Header:
        return <SubSectionHeader label={label} />;
      case FormUIOptions.TextArea:
        return (
          <TextArea
            name={name}
            label={label}
            description={description}
            layout={layout}
            specialInstructions={specialInstructions}
            // hint={"Optional Hint here"}
            required={required}
          />
        );
      case FormUIOptions.UnitText:
      case FormUIOptions.PercentTextInput:
        return (
          <PercentTextInput
            name={name}
            label={label}
            description={description}
            layout={layout}
            type="text"
            required={required}
          />
        );
      case FormUIOptions.Calculated:
        return (
          <CalculatedField
            field={elem}
            externalData={externalData}
            layout={layout}
          />
        );
      case FormUIOptions.UnitMoney:
        return (
          <UnitMoneyInput
            name={name}
            label={label}
            derivedFrom={derivedFrom || null}
            description={description}
            tooltipFormula={tooltipFormula}
            tooltipHeading={tooltipHeading}
            externalData={externalData}
            selectOptions={selectOptions}
            layout={layout}
            type="text"
            required={required}
            showAltValue={showAltValue}
            preValue={preValue}
          />
        );
      case FormUIOptions.UnitTime:
        return (
          <UnitTimeInput
            name={name}
            label={label}
            description={description}
            tooltipFormula={tooltipFormula}
            tooltipHeading={tooltipHeading}
            externalData={externalData}
            selectOptions={selectOptions}
            layout={layout}
            type="text"
            required={required}
            preValue={preValue}
          />
        );

      case FormUIOptions.Number:
        return (
          <NumberInput
            name={name}
            label={label}
            description={description}
            tooltipFormula={tooltipFormula}
            tooltipHeading={tooltipHeading}
            layout={layout}
            unit={unit}
            required={required}
            defaultValue={defaultValue}
            preValue={preValue}
            maxValue={maxValue}
          />
        );
      case FormUIOptions.Date:
        return (
          <TextInput
            name={name}
            label={label}
            description={description}
            type="date"
            layout={layout}
            required={required}
            preValue={preValue}
          />
        );
      case FormUIOptions.Money:
        return (
          <MoneyInput
            name={name}
            label={label}
            description={description}
            unit={unit}
            layout={layout}
            type="text"
            required={required}
            preValue={preValue}
            maxValue={maxValue}
          />
        );
      case FormUIOptions.Place:
        return <GooglePlaces name={name} label={label} layout={layout} />;
      case FormUIOptions.Select:
        return (
          <Select
            options={options}
            layout={layout}
            defaultLabel={defaultLabel}
            name={name}
            label={label}
            description={description}
            tooltipFormula={tooltipFormula}
            tooltipHeading={tooltipHeading}
            required={required}
            preValue={preValue}
          />
        );
      case FormUIOptions.Radio:
        return (
          <RadioButtons
            options={options}
            defaultLabel={defaultLabel || 'Choose an Option'}
            name={name}
            label={label}
            layout={layout}
            description={description}
            required={required}
          />
        );
      case FormUIOptions.RadioBigGroup:
        return (
          <RadioBigGroup
            options={options}
            defaultLabel={defaultLabel || 'Choose an Option'}
            name={name}
            label={label}
            layout={layout}
            description={description}
            required={required}
          />
        );
      case FormUIOptions.YesNoExplain:
        return (
          <YesNoExplain
            name={name}
            label={label}
            layout={layout}
            description={description}
            describeMessage={describeMessage}
            specialInstructions={specialInstructions}
            required={required}
          />
        );
      case FormUIOptions.YesNo:
        return (
          <YesNo
            name={name}
            label={label}
            description={description}
            tooltipFormula={tooltipFormula}
            tooltipHeading={tooltipHeading}
            layout={layout}
            specialInstructions={specialInstructions}
            required={required}
            preValue={preValue}
            defaultValue={defaultValue}
            yesLabel={yesLabel}
            noLabel={noLabel}
            hideLabel={hideLabel}
            toggleColour={toggleColour}
            afterPosition={afterPosition}
          />
        );
      case FormUIOptions.Explain:
        return (
          <Explain
            name={name}
            label={label}
            description={description}
            layout={layout}
            specialInstructions={specialInstructions}
            required={required}
          />
        );
      case FormUIOptions.Toggle:
        return (
          <Toggle
            name={name}
            label={label}
            description={description}
            layout={layout}
            specialInstructions={specialInstructions}
            required={required}
          />
        );
      case FormUIOptions.TableModal:
        return (
          <TableInputModal
            name={name}
            label={label}
            description={description}
            tooltipFormula={tooltipFormula}
            tooltipHeading={tooltipHeading}
            modalDescription={modalDescription}
            itemName={itemName}
            layout={layout}
            specialInstructions={specialInstructions}
            tableSchema={tableSchema}
            externalData={externalData}
            required={required}
            preValue={preValue}
          />
        );
      case FormUIOptions.TableInline:
        return (
          <TableInputInline
            name={name}
            label={label}
            description={description}
            tooltipFormula={tooltipFormula}
            tooltipHeading={tooltipHeading}
            itemName={itemName}
            specialInstructions={specialInstructions}
            tableSchema={tableSchema}
            externalData={externalData}
            required={required}
            preValue={preValue}
          />
        );
      case FormUIOptions.BeforeAfterTableInline:
        return (
          <BeforeAfterTableInput
            name={name}
            label={label}
            description={description}
            itemName={itemName}
            layout={layout}
            specialInstructions={specialInstructions}
            tableSchema={tableSchema}
            externalData={externalData}
            required={required}
            sectionHeaders={sectionHeadersInline}
            condition={condition}
          />
        );
      case FormUIOptions.BeforeAfterTableModal:
        return (
          <BeforeAfterTableModal
            name={name}
            label={label}
            description={description}
            modalDescription={modalDescription}
            itemName={itemName}
            tableSchema={tableSchema}
            preValue={preValue}
            externalData={externalData}
            sectionHeaders={sectionHeadersModal}
            condition={condition}
          />
        );
      case FormUIOptions.PictureGallery:
        return (
          <GalleryInput
            name={name}
            label={label}
            description={description}
            layout={layout}
            specialInstructions={specialInstructions}
            required={required}
          />
        );
      case FormUIOptions.TableSection:
        return (
          <TableSection
            label={label}
            schema={schema}
            externalData={externalData}
          />
        );

      case FormUIOptions.CalculatedSection:
        return <CalculatedSection schema={schema} data={externalData} />;

      case FormUIOptions.LinkButton:
        return <LinkButton to={to} label={label} />;
      default:
        console.error('Error rendering input: invalid type ', type);
        return null;
    }
  };

  if (show) {
    return (
      <>
        {elem.groupStart && (
          <div className="w-full p-1">
            <div className="h-[1px] w-full  bg-neutral-50 relative rounded-sm"></div>
          </div>
        )}
        {getInput()}
        {elem.groupEnd && (
          <div className="w-full p-1">
            <div className="h-[1px] bg-neutral-50 w-full relative rounded-sm"></div>
          </div>
        )}
      </>
    );
  } else {
    return null;
  }
}
